import React, { useState } from "react";
import { zoneStatusColor, moistureColor, battColor } from "./irrigationHelpers";

const STATUS_COLOR = {
  ON:        "#10b981",
  SCHEDULED: "#f59e0b",
  STANDBY:   "#3b82f6",
  FAULT:     "#ef4444",
};

const IrrFieldMapPanel = ({ d }) => {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter]   = useState("All");

  const filters = ["All", "ON", "SCHEDULED", "STANDBY", "FAULT"];
  const visible = filter === "All" ? d.zones : d.zones.filter(z => z.status === filter);
  const sel = selected ? d.zones.find(z => z.id === selected) : null;
  const selSt = sel ? zoneStatusColor(sel.status) : null;

  return (
    <div className="irr-map-layout">

      {/* ── Filter chips ── */}
      <div className="irr-map-filter">
        {filters.map(f => (
          <button key={f} className={`irr-filter-chip ${filter === f ? "irr-filter-active" : ""}`}
            style={filter === f && f !== "All" ? { background: STATUS_COLOR[f] + "22", color: STATUS_COLOR[f], borderColor: STATUS_COLOR[f] } : {}}
            onClick={() => setFilter(f)}>{f === "All" ? "All Zones" : f === "ON" ? "🟢 ON" : f === "SCHEDULED" ? "🟡 Scheduled" : f === "STANDBY" ? "🔵 Standby" : "🔴 Fault"}</button>
        ))}
        <span className="irr-map-legend-note">Click a zone to control</span>
      </div>

      <div className="irr-map-body">
        {/* ── SVG Field Map ── */}
        <div className="irr-map-svg-wrap">
          <svg viewBox="0 0 100 100" className="irr-map-svg" preserveAspectRatio="xMidYMid meet">
            {/* Farm background */}
            <rect x="0" y="0" width="100" height="100" fill="#f0fdf4" rx="2" />

            {/* Water source (top-left) */}
            <circle cx="4" cy="4" r="3" fill="#3b82f6" opacity="0.85" />
            <text x="4" y="4.5" textAnchor="middle" fontSize="1.8" fill="white" fontWeight="bold">W</text>
            <text x="4" y="9" textAnchor="middle" fontSize="1.8" fill="#1d4ed8">Borewell</text>

            {/* Pump */}
            <rect x="2" y="11" width="6" height="4" fill="#f59e0b" rx="1" />
            <text x="5" y="13.5" textAnchor="middle" fontSize="1.6" fill="white" fontWeight="bold">P</text>
            <text x="5" y="17.5" textAnchor="middle" fontSize="1.6" fill="#92400e">Pump</text>

            {/* Main pipeline */}
            <line x1="5" y1="15" x2="5" y2="96" stroke="#3b82f6" strokeWidth="0.5" strokeDasharray="1,1" opacity="0.4" />
            <line x1="5" y1="40" x2="96" y2="40" stroke="#3b82f6" strokeWidth="0.5" strokeDasharray="1,1" opacity="0.4" />
            <line x1="5" y1="68" x2="96" y2="68" stroke="#3b82f6" strokeWidth="0.5" strokeDasharray="1,1" opacity="0.4" />

            {/* Zones */}
            {d.zones.map(z => {
              const col = STATUS_COLOR[z.status];
              const isSelected = selected === z.id;
              const isHidden = filter !== "All" && z.status !== filter;
              return (
                <g key={z.id} onClick={() => setSelected(z.id === selected ? null : z.id)} style={{ cursor: "pointer" }} opacity={isHidden ? 0.2 : 1}>
                  <rect x={z.x} y={z.y} width={z.w} height={z.h}
                    fill={col + "28"} stroke={col} strokeWidth={isSelected ? 0.8 : 0.4}
                    rx="1" />
                  {/* Animated fill for ON zones */}
                  {z.status === "ON" && (
                    <rect x={z.x + 0.5} y={z.y + 0.5} width={z.w - 1} height={z.h - 1}
                      fill={col + "18"} rx="0.5">
                      <animate attributeName="opacity" values="0.3;0.7;0.3" dur="2s" repeatCount="indefinite" />
                    </rect>
                  )}
                  <text x={z.x + z.w / 2} y={z.y + z.h / 2 - 2} textAnchor="middle" fontSize="2" fontWeight="600" fill={col}>{z.id}</text>
                  <text x={z.x + z.w / 2} y={z.y + z.h / 2 + 2.5} textAnchor="middle" fontSize="1.6" fill="#374151">{z.name.split(" ").slice(0,2).join(" ")}</text>
                  <text x={z.x + z.w / 2} y={z.y + z.h / 2 + 6} textAnchor="middle" fontSize="1.5" fill="#6b7280">💧{z.soilMoisture}%</text>
                  {/* Node pin */}
                  <circle cx={z.x + z.w - 2.5} cy={z.y + 2.5} r="2" fill="#8b5cf6" opacity="0.85" />
                  <text x={z.x + z.w - 2.5} y={z.y + 3.1} textAnchor="middle" fontSize="1.4" fill="white" fontWeight="bold">N</text>
                  {isSelected && (
                    <rect x={z.x - 0.5} y={z.y - 0.5} width={z.w + 1} height={z.h + 1}
                      fill="none" stroke="white" strokeWidth="1" rx="1.5" />
                  )}
                </g>
              );
            })}

            {/* Legend */}
            <rect x="1" y="85" width="28" height="14" fill="white" rx="1" opacity="0.9" />
            <text x="3" y="88.5" fontSize="1.8" fill="#374151" fontWeight="600">Legend</text>
            {[["ON", "#10b981","🟢"], ["Scheduled","#f59e0b","🟡"], ["Standby","#3b82f6","🔵"], ["Fault","#ef4444","🔴"]].map(([lbl, col], i) => (
              <g key={lbl}>
                <rect x="3" y={91 + i * 1.8} width="2" height="1.5" fill={col} rx="0.3" />
                <text x="6.5" y={92.2 + i * 1.8} fontSize="1.5" fill="#374151">{lbl}</text>
              </g>
            ))}
          </svg>
        </div>

        {/* ── Detail panel ── */}
        <div className="irr-map-detail">
          {sel ? (
            <div className="irr-zone-detail">
              <div className="irr-zd-header" style={{ borderColor: selSt.color }}>
                <div>
                  <div className="irr-zd-id">{sel.id} — {sel.name}</div>
                  <div className="irr-zd-crop">Crop: {sel.crop} · {sel.area} ha</div>
                </div>
                <span className="irr-badge" style={{ background: selSt.bg, color: selSt.color, fontSize: 13 }}>{selSt.label}</span>
              </div>

              {/* Sensors */}
              <div className="irr-zd-sensors">
                {[
                  { label: "Soil Moisture", value: `${sel.soilMoisture}%`, sub: `Target: ${sel.soilMoistureTarget}%` },
                  { label: "Pressure",      value: `${sel.pressure} bar`,  sub: sel.pressure < 2 ? "⚠ Low" : "Normal" },
                  { label: "Flow Rate",     value: sel.flowRate > 0 ? `${sel.flowRate} L/min` : "0 (Closed)", sub: "" },
                  { label: "Valve",         value: sel.valveStatus,         sub: "" },
                  { label: "Water Delivered", value: `${sel.waterVolume} L`, sub: "Today" },
                  { label: "Run Duration",  value: `${sel.duration} min`,   sub: "Today" },
                  { label: "Node Battery",  value: `${sel.nodeBattery}%`,   sub: sel.nodeId },
                  { label: "LoRa RSSI",     value: `${sel.rssi} dBm`,       sub: `SNR: ${sel.snr} dB` },
                ].map(s => (
                  <div key={s.label} className="irr-zd-sensor">
                    <div className="irr-zd-s-label">{s.label}</div>
                    <div className="irr-zd-s-value">{s.value}</div>
                    {s.sub && <div className="irr-zd-s-sub">{s.sub}</div>}
                  </div>
                ))}
              </div>

              {/* Controls */}
              <div className="irr-zd-controls">
                <div className="irr-zd-ctrl-title">Controls</div>
                <div className="irr-ctrl-row">
                  <button className="irr-ctrl-btn irr-ctrl-green">▶ Start Irrigation</button>
                  <button className="irr-ctrl-btn irr-ctrl-red">⏹ Stop Irrigation</button>
                </div>
                <div className="irr-ctrl-row">
                  <button className="irr-ctrl-btn irr-ctrl-blue">🔓 Open Valve</button>
                  <button className="irr-ctrl-btn irr-ctrl-gray">🔒 Close Valve</button>
                </div>
                <div className="irr-ctrl-mode">
                  <span>Mode:</span>
                  <button className={`irr-mode-btn ${sel.mode === "Auto" ? "irr-mode-active" : ""}`}>Auto</button>
                  <button className={`irr-mode-btn ${sel.mode === "Manual" ? "irr-mode-active" : ""}`}>Manual</button>
                </div>
                <div className="irr-ctrl-inputs">
                  <label>Duration (min)<input type="number" defaultValue={30} className="irr-ctrl-input" /></label>
                  <label>Target Moisture %<input type="number" defaultValue={sel.soilMoistureTarget} className="irr-ctrl-input" /></label>
                </div>
              </div>
            </div>
          ) : (
            <div className="irr-map-no-sel">
              <div className="irr-no-sel-icon">🗺</div>
              <div className="irr-no-sel-title">Click a zone</div>
              <div className="irr-no-sel-sub">Select any zone on the map to view sensor data and controls</div>
              <div className="irr-map-quick-stats">
                {["ON","SCHEDULED","STANDBY","FAULT"].map(s => {
                  const count = d.zones.filter(z => z.status === s).length;
                  const col = STATUS_COLOR[s];
                  return <div key={s} className="irr-mqs-item" style={{ borderColor: col }}><span style={{ color: col }}>{count}</span><small>{s}</small></div>;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IrrFieldMapPanel;
